import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useAuth } from './useAuth';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { UpdateProfileRequest, UpdatePasswordRequest } from '../types/auth';

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateProfileRequest) => {
      const response = await apiService.updateProfile(data);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update profile'));
      }
      return response.data;
    },
    onSuccess: (updatedUser) => {
      // Invalidate auth user query
      queryClient.invalidateQueries({ queryKey: invalidateQueries.auth.user() });
      
      // If the user is in the users list (admin portal), invalidate user queries
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(user.id) });
        queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
        queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
        queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
        queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
      }

      // Update the query cache with fresh user data
      queryClient.setQueryData(queryKeys.auth.user, updatedUser);
    },
  });
}

export function useUpdatePassword() {
  return useMutation({
    mutationFn: async (data: UpdatePasswordRequest) => {
      const response = await apiService.updatePassword(data);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to change password'));
      }
      return response.data;
    },
  });
}